import type { MouseEventHandler } from 'react';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import type { BFFHttpError } from '../../../../bff-api/model/BFFHttpError';

export enum BloomreachProductType {
    hardgood = 'hardgood',
    culinary = 'culinary',
    recipe = 'recipe',
}

export interface SearchParams {
    productType?: BloomreachProductType;
    q?: string;
    categoryId?: string;
    price?: string[];
    brands?: string[];
    colors?: string[];
    sizes?: string[];
    materials?: string[];
    rating?: string[];
    sort?: string;
    page?: number;
    pageSize?: number;
    categories?: string[];
    bopisStoreId?: string;
    sddStoreId?: string;
}

export interface CategoryNode {
    id: string;
    name: string;
    url: string;
    count: number;
    parent: string;
    children?: CategoryNode[];
    type?: string;
}

export interface SearchPrices {
    price: [number, number] | [number];
    regularPrice?: [number, number] | [number];
    suggestedPrice?: [number, number] | [number];
    salePrice?: [number, number] | [number];
}

export interface SearchVariant {
    id: string;
    c_pwaUrl: string;
    c_badge: SearchBadge;
    c_imageUrl: string;
    material?: string;
    refinementColor?: string;
    colorName?: string;
    swatchColor?: string;
    size?: string;
    searchPrices: SearchPrices;
    c_promotionHTMLs?: string[];
    sku_images?: string[];
}

export interface SearchBadge {
    badgeLabel?: string;
    badgeStyle?: string;
    badgeShowOnPDP: boolean;
    badgeShowOnPLP: boolean;
}

export type SearchProduct = {
    c_pwaUrl: string;
    c_badge: SearchBadge;
    brand: string;
    longDescription: string;
    id: string;
    name: string;
    c_variantAttributeLink?: 'itemColor' | 'itemSize';
    c_imageUrl: string;
    c_turntoAverageRating: string;
    c_variants: SearchVariant[];
    c_turntoTotalReviewCount?: number;
    suggestedPrice?: number[];
    pageNumber?: number;
    c_promotionHTMLs?: string[];
} & (
    | {
          productType: BloomreachProductType.hardgood;
          searchPrices: SearchPrices;
      }
    | {
          productType: BloomreachProductType.culinary;
          cuisineType?: string;
          searchPrices: SearchPrices;
      }
    | { productType: BloomreachProductType.recipe }
);

export type RangeFilter = { start: string | number; end: string | number; count: number };
export type SearchResult = {
    page: {
        total: number;
        page: number;
        pageSize: number;
    };
    items: SearchProduct[];
    filters: {
        categories: Array<CategoryNode>;
        byName: {
            brand: Array<{ name: string; count: number }>;
            color: Array<{ name: string; count: number }>;
            size: Array<{ name: string; count: number }>;
            material: Array<{ name: string; count: number }>;
        };
        byRange: {
            price: Array<RangeFilter>;
            rating: Array<RangeFilter>;
        };
    };
    querySuggestions: string[];
    sort_fields: string[];
    redirectUrl?: string;
};

export type UseSearchResult = Pick<
    UseInfiniteQueryResult<SearchResult, BFFHttpError>,
    | 'isLoading'
    | 'hasPreviousPage'
    | 'hasNextPage'
    | 'isFetchingNextPage'
    | 'isFetchingPreviousPage'
    | 'fetchPreviousPage'
    | 'error'
    | 'isFetching'
> & {
    productType: BloomreachProductType;
    currentPage: number;
    total: number;
    items: SearchProduct[];
    filters?: SearchResult['filters'];
    categoriesMap: Record<string, CategoryNode>;
    nextPageUrl?: string;
    handleNextPage: MouseEventHandler<HTMLButtonElement>;
    handlePreviousPage: MouseEventHandler<HTMLButtonElement>;
    activeFilters: Filter[];
    searchParams: SearchParams;
    isCustomPrice: boolean;
    redirectUrl?: string;
} & SearchSideEffects;

export type Filter = {
    type: string;
    value: string;
    label: string;
};

export type SearchSideEffectsProps = {
    updateURL: boolean;
};

export interface SearchSideEffects {
    onSelected: () => void;
    onClearAllFilters: () => void;
    onRemoveFilter: (type: string, value?: string) => void;
    onChangeFilter: (type: string, value: string) => void;
    onChangeRangeFilter: (type: string, value: string) => void;
    onChangeParam: (type: string, value: string, updateURL?: boolean) => void;
    isFilterSelected: (type: string, value: string) => boolean;
    activeFilters: Filter[];
    onBulkChangeFilter: (filters: Array<{ type: string; value: string }>) => void;
}
